// grupos/editar-grupo

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";

import FormPage from "ui/FormPage";
import FormTextField from "ui/FormTextField";
import { PrimaryButton, OutlineButton } from "ui/Buttons";

import api from "services/api";
import { getGrupoById, updateGrupo } from "../services/grupos";

/* ---------------- helpers (basado en certificaciones) ---------------- */

const toYMD = (v) => {
  if (!v) return "";
  const d = typeof v === "string" ? new Date(v) : v;
  if (Number.isNaN(+d)) {
    const m = String(v).match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : "";
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const guessMimeFromName = (name = "") => {
  if (/\.png$/i.test(name)) return "image/png";
  if (/\.jpe?g$/i.test(name)) return "image/jpeg";
  if (/\.gif$/i.test(name)) return "image/gif";
  if (/\.webp$/i.test(name)) return "image/webp";
  return "image/*";
};
/** Construye URL a /attachments/<filename> o sin extensión */
const attachmentUrl = (name = "", tryWithoutExt = false) => {
  if (!name) return null;
  const n = tryWithoutExt ? String(name).replace(/\.[^.]+$/, "") : name;
  return `/attachments/${encodeURIComponent(n)}`;
};

/** Intenta resolver vista previa de imagen protegida por token */
async function resolveImagePreview(record, field) {
  const filename = record?.[field];
  if (!filename) return null;

  // 1) URL directa en payload
  const directUrl =
    record?.[`${field}_url`] || record?.[`${field}Url`] || null;
  if (directUrl) {
    return { url: directUrl, type: guessMimeFromName(directUrl), src: directUrl };
  }

  // 2) Descarga autenticada como blob desde candidatos
  const candidates = [
    attachmentUrl(filename, false),
    attachmentUrl(filename, true),
    `/files/${encodeURIComponent(filename)}`,
    `/uploads/${encodeURIComponent(filename)}`,
    `/storage/${encodeURIComponent(filename)}`,
    `/groups/${encodeURIComponent(record.id)}/file/profile_picture`,
  ];

  for (const url of candidates) {
    try {
      const res = await api.get(url, { responseType: "blob" }); // token via axios
      if (res?.status === 200 && res?.data) {
        const blob = res.data;
        const type = blob.type || guessMimeFromName(filename);
        const objectUrl = URL.createObjectURL(blob);
        return { url: objectUrl, type, src: url, _blob: true };
      }
    } catch {
      // probar siguiente
    }
  }
  return null;
}

const parseErrs = (err) => {
  const out = [];
  const d = err?.response?.data ?? err;
  const push = (m) => m && out.push(String(m));
  if (typeof d === "string") push(d);
  else if (Array.isArray(d)) d.forEach((x) => push(x?.message || JSON.stringify(x)));
  else if (Array.isArray(d?.errors)) d.errors.forEach((x) => push(x?.message || JSON.stringify(x)));
  else if (d?.message) push(d.message);
  else push(err?.message || "Error");
  return out;
};

/* ---------------- componente ---------------- */

const MAX_FILE_MB = 10;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

export default function EditarGrupo() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [createdAt, setCreatedAt] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  // nombre de archivo/URL que viene del backend
  const [profileName, setProfileName] = useState("");
  // preview actual (puede venir de blob autenticado o url directa)
  const [profilePreview, setProfilePreview] = useState(null);

  // reemplazo nuevo
  const [newProfileFile, setNewProfileFile] = useState(null);
  const [newProfileURL, setNewProfileURL] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setErrors([]);
      try {
        const g = await getGrupoById(id);
        if (cancel) return;

        setName(g?.name ?? g?.nombre ?? "");
        setCreatedAt(g?.created_at ?? g?.createdAt ?? "");

        // intenta detectar dónde viene la imagen
        const profile =
          g?.profile_picture || g?.profilePicture || g?.profile || "";
        const directUrl =
          g?.profile_picture_url || g?.profilePictureUrl || g?.imageUrl || "";

        setProfileName(profile || directUrl || "");

        const preview = await resolveImagePreview(
          { ...g, profile_picture: profile, profile_picture_url: directUrl, id },
          "profile_picture"
        );
        if (!cancel) setProfilePreview(preview);
      } catch (e) {
        if (!cancel) setErrors(parseErrs(e));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [id]);

  // limpia objectURLs locales y de blobs remotos
  useEffect(() => () => { try { if (newProfileURL) URL.revokeObjectURL(newProfileURL); } catch {} }, [newProfileURL]);
  useEffect(() => () => { try { if (profilePreview?._blob) URL.revokeObjectURL(profilePreview.url); } catch {} }, [profilePreview]);

  const isValid = useMemo(() => !!name.trim(), [name]);

  const onPickNew = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setErrors([`El archivo seleccionado supera el límite de ${MAX_FILE_MB} MB.`]);
      return;
    }
    const url = URL.createObjectURL(file);
    setNewProfileFile(file);
    setNewProfileURL(url);
    setProfilePreview({ url, type: file.type || "image/*", src: file.name, _local: true });
    setProfileName(file.name);
  };

const onSubmit = async (e) => {
  e.preventDefault();
  if (!isValid) return;
  setSaving(true); setErrors([]);

  try {
    if (newProfileFile) {
      // Con archivo → multipart
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("profile_picture", newProfileFile);
      await updateGrupo(id, fd, "PUT");
    } else {
      // Sin archivo → JSON (tu service ya lo maneja como application/json)
      await updateGrupo(id, { name: name.trim() }, "PUT");
    }

    navigate("/catalogos/grupos", { replace: true });
  } catch (e) {
    setErrors(parseErrs(e));
  } finally {
    setSaving(false);
  }
};


  return (
    <FormPage
      title="Editar grupo"
      onBack={() => navigate("/catalogos/grupos")}
      pageMaxWidth={1100}
      contentMaxWidth={900}
      actions={
        <>
          <PrimaryButton type="submit" form="form-grupo" disabled={!isValid || saving || loading}>
            {saving ? "Guardando..." : "Guardar"}
          </PrimaryButton>
          <OutlineButton onClick={() => navigate("/catalogos/grupos")}>Cancelar</OutlineButton>
        </>
      }
    >
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.map((m, i) => <div key={i}>{m}</div>)}
        </Alert>
      )}

      <form id="form-grupo" onSubmit={onSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormTextField
              label="Nombre*"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormTextField label="Creado" value={toYMD(createdAt)} disabled />
          </Grid>
        </Grid>

        <Card variant="outlined" sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Imagen del grupo
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={3}>
            {/* Imagen actual / preview */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Imagen actual</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {profileName || "—"}
              </Typography>

              <Box sx={{ mt: 1 }}>
                {profilePreview?.url ? (
                  <Box sx={{ border: "1px solid #eee", p: 1, borderRadius: 1, display: "inline-block" }}>
                    <img
                      src={profilePreview.url}
                      alt={profileName || "Imagen actual"}
                      style={{ maxHeight: 240, maxWidth: "100%", display: "block", objectFit: "cover" }}
                    />
                  </Box>
                ) : profileName ? (
                  // Fallback: intenta /attachments/<name> (sin blob) por si fuera público
                  <Box sx={{ border: "1px solid #eee", p: 1, borderRadius: 1, display: "inline-block" }}>
                    <img
                      src={attachmentUrl(profileName) || ""}
                      alt={profileName}
                      style={{ maxHeight: 240, maxWidth: "100%", display: "block", objectFit: "cover" }}
                      onError={(e) => { e.currentTarget.src = attachmentUrl(profileName, true) || ""; }}
                    />
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">Sin imagen o no disponible</Typography>
                )}
              </Box>
            </Grid>

            {/* Reemplazar */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Reemplazar imagen</Typography>
              <Button variant="outlined" component="label" sx={{ mt: 1 }}>
                Seleccionar imagen…
                <input type="file" accept="image/*" hidden onChange={onPickNew} />
              </Button>

              {newProfileURL && (
                <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
                  <img
                    src={newProfileURL}
                    alt="Nueva selección"
                    style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 8 }}
                  />
                  <Button
                    variant="text"
                    color="error"
                    onClick={() => {
                      setNewProfileFile(null);
                      if (newProfileURL) URL.revokeObjectURL(newProfileURL);
                      setNewProfileURL("");
                      // restaurar preview previo si existía
                      setProfilePreview(null);
                    }}
                  >
                    Quitar selección
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </Card>
      </form>
    </FormPage>
  );
}
